import { useEffect, useRef, useState } from 'react';

const THRESHOLD = 80;
const DAMPING = 0.5;
const MAX_TRAVEL = 140;

/**
 * Touch-gesture pull-to-refresh. Attaches to the scrollable element passed via ref.
 * Only engages when the element is scrolled to the top. Returns the distance
 * (0..MAX_TRAVEL) and whether we're currently refreshing so the caller can render
 * the indicator and translate the list accordingly.
 */
export const usePullToRefresh = (scrollRef, onRefresh, { windowScroll = false } = {}) => {
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const isPulling = useRef(false);
  // Handlers read the live distance from this ref rather than closing over
  // state - the effect used to list `distance` in its deps, tearing down and
  // re-adding all four listeners on EVERY pulled frame.
  const distanceRef = useRef(0);

  useEffect(() => {
    const el = scrollRef?.current;
    if (!el) return undefined;

    // windowScroll: pages where the document itself scrolls (the marketplace)
    // attach the gesture to a content wrapper but must gate on the window's
    // scroll position, not the wrapper's own (always-zero) scrollTop.
    const getScrollTop = () => (windowScroll ? (window.scrollY || document.documentElement.scrollTop) : el.scrollTop);

    const setDist = (v) => {
      distanceRef.current = v;
      setDistance(v);
    };

    const handleTouchStart = (e) => {
      if (getScrollTop() > 0) {
        startY.current = null;
        return;
      }
      startY.current = e.touches[0].clientY;
      isPulling.current = true;
    };

    const handleTouchMove = (e) => {
      if (!isPulling.current || startY.current == null) return;
      if (getScrollTop() > 0) {
        startY.current = null;
        isPulling.current = false;
        setDist(0);
        return;
      }
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) {
        setDist(0);
        return;
      }
      const eased = Math.min(dy * DAMPING, MAX_TRAVEL);
      setDist(eased);
      if (eased > 4) {
        // Prevent the browser's native rubber-band so our gesture wins
        if (e.cancelable) e.preventDefault();
      }
    };

    const handleTouchEnd = async () => {
      if (!isPulling.current) return;
      isPulling.current = false;
      const triggered = distanceRef.current >= THRESHOLD;
      if (triggered) {
        setRefreshing(true);
        setDist(THRESHOLD);
        try {
          await onRefresh?.();
        } finally {
          setRefreshing(false);
          setDist(0);
        }
      } else {
        setDist(0);
      }
      startY.current = null;
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd);
    el.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      el.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [scrollRef, onRefresh, windowScroll]);

  return {
    distance,
    refreshing,
    progress: Math.min(distance / THRESHOLD, 1),
    armed: distance >= THRESHOLD,
  };
};
